import { useId, useRef, useState, type CSSProperties, type ReactElement } from "react";
import { ChevronDown, LogOut, Settings, Settings2 } from "lucide-react";
import { color, space } from "@tokens/index";
import { Text } from "@primitives/Text";
import { Avatar } from "@primitives/Avatar";
import { Button } from "@primitives/Button";
import { Menu, MenuItem, MenuSeparator } from "@primitives/overlay";
import { useViewport } from "@primitives/viewport";

// ---------------------------------------------------------------------------
// Public types
//
// Presentational composition: it owns no auth or navigation logic. The
// experience shell feeds it the identity and the handlers.
// ---------------------------------------------------------------------------

export interface AccountMenuProps {
    readonly userName: string;
    readonly userEmail: string;
    /** Opens the per-user settings pages. */
    readonly onOpenSettings: () => void;
    /** Omit to hide the Admin row. */
    readonly onOpenAdmin?: () => void;
    readonly onSignOut: () => void;
}

const identityStyle: CSSProperties = {
    display: "flex",
    alignItems: "center",
    gap: space[3],
    padding: `${space[2]} ${space[3]}`,
    minWidth: 0,
};

const identityTextStyle: CSSProperties = {
    display: "grid",
    gap: 2,
    minWidth: 0,
};

// The name never shrinks below its cap, so the bar's actions track stays a
// fixed width and the brand wordmark absorbs any shrink.
const triggerNameStyle = (wide: boolean): CSSProperties => ({
    maxWidth: wide ? 160 : 112,
    flexShrink: 0,
});

/**
 * Account menu: identity, Settings, Admin, and Sign out.
 *
 * The trigger shows the avatar and name on `expanded` and wider, and the
 * avatar alone below. The panel is a `Menu`, so it floats in a portal and
 * presents as a bottom sheet on `compact`.
 */
export function AccountMenu(props: AccountMenuProps): ReactElement {
    const { userName, userEmail, onOpenSettings, onOpenAdmin, onSignOut } = props;
    const { viewport, isAtLeast } = useViewport();
    const showName = isAtLeast("expanded");
    const [isOpen, setIsOpen] = useState(false);
    const triggerRef = useRef<HTMLButtonElement>(null);
    const triggerId = useId();

    return (
        <>
            <Button
                ref={triggerRef}
                id={triggerId}
                variant="ghost"
                size="md"
                iconOnly={!showName}
                aria-haspopup="menu"
                aria-expanded={isOpen}
                aria-label="Account menu"
                data-topbar-account-trigger=""
                {...(showName ? { style: { paddingInline: space[2] } } : {})}
                onClick={() => setIsOpen((prev) => !prev)}
            >
                <Avatar alt={userName} size="sm" />
                {showName && (
                    <>
                        <Text
                            role="bodySm"
                            weight="semibold"
                            truncate
                            style={triggerNameStyle(viewport === "wide")}
                        >
                            {userName}
                        </Text>
                        <ChevronDown
                            size={14}
                            strokeWidth={2.25}
                            aria-hidden="true"
                            style={{ color: color.fg.muted, flexShrink: 0 }}
                        />
                    </>
                )}
            </Button>

            <Menu
                open={isOpen}
                onClose={() => setIsOpen(false)}
                anchorRef={triggerRef}
                placement="bottom-end"
                labelledBy={triggerId}
                minWidth={260}
                sheetOnCompact
                testId="account-menu"
            >
                <AccountIdentityRow userName={userName} userEmail={userEmail} />
                <MenuSeparator />
                <AccountMenuItems
                    onOpenSettings={onOpenSettings}
                    onSignOut={onSignOut}
                    {...(onOpenAdmin !== undefined ? { onOpenAdmin } : {})}
                />
            </Menu>
        </>
    );
}

export interface AccountIdentityRowProps {
    readonly userName: string;
    readonly userEmail: string;
}

/** Non-interactive row that names the signed-in identity. */
export function AccountIdentityRow({ userName, userEmail }: AccountIdentityRowProps): ReactElement {
    return (
        <div style={identityStyle} data-account-identity="">
            <Avatar alt={userName} size="md" />
            <div style={identityTextStyle}>
                <Text role="bodySm" weight="semibold" truncate>
                    {userName}
                </Text>
                <Text role="caption" tone="subtle" truncate>
                    {userEmail}
                </Text>
            </div>
        </div>
    );
}

export type AccountMenuItemsProps = Omit<AccountMenuProps, "userName" | "userEmail">;

/**
 * The account action rows: Settings, Admin, Sign out. Rendered inside a
 * `Menu` by `AccountMenu` and by the compact overflow menu.
 */
export function AccountMenuItems({
    onOpenSettings,
    onOpenAdmin,
    onSignOut,
}: AccountMenuItemsProps): ReactElement {
    return (
        <>
            <MenuItem
                icon={<Settings size={16} strokeWidth={2} aria-hidden="true" />}
                label="Settings"
                description="Appearance, notifications, and account"
                onSelect={onOpenSettings}
                testId="account-menu-settings"
            />
            {onOpenAdmin !== undefined && (
                <MenuItem
                    icon={<Settings2 size={16} strokeWidth={2} aria-hidden="true" />}
                    label="Admin"
                    description="Queues, management groups, and routing"
                    onSelect={onOpenAdmin}
                    testId="account-menu-admin"
                />
            )}
            <MenuSeparator />
            <MenuItem
                icon={<LogOut size={16} strokeWidth={2} aria-hidden="true" />}
                label="Sign out"
                tone="danger"
                onSelect={onSignOut}
                testId="account-menu-sign-out"
            />
        </>
    );
}
